import { getAllocationRequests } from "../approvals";

const FILTER_TO_STATUS = {
  "all":      undefined,
  "at-pmo":   "SUBMITTED,MOVE_PENDING",
  "at-scg":   "PMO_APPROVED",
  "active":   "ACTIVE",
  "rejected": "REJECTED",
};

const API_STATUS_TO_UI = {
  SUBMITTED:    "AT_PMO",
  MOVE_PENDING: "AT_PMO",
  PMO_APPROVED: "AT_SCG",
  ACTIVE:       "ACTIVE",
  REJECTED:     "REJECTED",
};

// Build pipeline dot states from status + approvals array
const buildApprovalStages = (apiStatus, approvals = []) => {
  const gates = approvals.map((a) => a.gate);
  return {
    submitted: true,
    pmoGate:   gates.includes("PMO") || ["ACTIVE"].includes(apiStatus),
    scgGate:   gates.includes("SCG") || apiStatus === "ACTIVE",
    active:    apiStatus === "ACTIVE",
  };
};

const relativeTime = (isoString) => {
  if (!isoString) return "";
  const diff  = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs  < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// Map real API response → shape RequestCard expects
const normalise = (r) => ({
  id:          r.requestId || r.id,
  requestCode: r.requestCode || r.requestId,

  status:    API_STATUS_TO_UI[r.status] || r.status,
  rawStatus: r.status,

  associate: {
    fullName:   r.associateName      || "",
    levelCode:  r.associateLevelCode || r.levelCode || "",
    levelTitle: r.roleTitle          || "",
    initials:   r.initials           || "",
  },

  position: {
    positionId:   r.positionId   || "",
    positionCode: r.positionCode || "",
    roleTitle:    r.roleTitle    || "",
    sowName:      r.sowName      || "",
    macName:      r.macName      || "",
    positionType: r.positionType || "BIL",
    levelCode:    r.levelCode    || "",
  },

  sow:         r.sowName     || "",
  accountName: r.accountName || null,
  fte:       r.fte       ?? 0,
  startDate: r.startDate || null,
  endDate:   r.endDate   || null,
  notes:     r.notes     || "",
  skills:    Array.isArray(r.deployedSkillNames) ? r.deployedSkillNames : [],

  rejectionReason: r.rejectionReason || "",

  // Submitter info
  submittedBy:    r.submittedByName || "",
  submittedByMac: r.macName         || "",
  submittedAgo:   relativeTime(r.submittedAt),
  submittedAt:    r.submittedAt     || null,

  // Parent request fields (present when this is a resubmission)
  parentRequestId:     r.parentRequestId     || null,
  parentRequestCode:   r.parentRequestCode   || null,
  parentRequestStatus: r.parentRequestStatus || null,

  // Full approvals array
  approvals: (r.approvals || []).map((a) => ({
    gate:          a.gate,
    decision:      a.decision,
    decidedByName: a.decidedByName,
    decidedAt:     a.decidedAt,
    comments:      a.comments || "",
    decidedAgo:    relativeTime(a.decidedAt),
    decidedAtFmt:  formatDateTime(a.decidedAt),
  })),

  approvalStages: buildApprovalStages(r.status, r.approvals || []),
});

// ─── Public API ───────────────────────────────────────────────────────────────

export const getStatusFlowSummary = async () => {
  const data = await getAllocationRequests();
  const counts = { atPmo: 0, atScg: 0, active: 0, rejected: 0, total: 0 };
  (data || []).forEach((r) => {
    counts.total += 1;
    if (r.status === "SUBMITTED" || r.status === "MOVE_PENDING") counts.atPmo += 1;
    if (r.status === "PMO_APPROVED") counts.atScg    += 1;
    if (r.status === "ACTIVE")       counts.active   += 1;
    if (r.status === "REJECTED")     counts.rejected += 1;
  });
  return counts;
};

export const getStatusFlow = async ({ filter = "all", search } = {}) => {
  const apiStatus = FILTER_TO_STATUS[filter];
  const params    = apiStatus ? { status: apiStatus } : {};

  const data = await getAllocationRequests(params);
  let results = (data || []).map(normalise);

  if (search) {
    const q = search.toLowerCase();
    results = results.filter((r) =>
      r.associate.fullName.toLowerCase().includes(q)  ||
      r.requestCode.toLowerCase().includes(q)          ||
      r.position.roleTitle.toLowerCase().includes(q)  ||
      r.position.sowName.toLowerCase().includes(q)
    );
  }

  return results;
};